import { create } from 'zustand';

/** After "Not now", stay quiet this long before asking again, so one dead tab doesn't nag on every request. */
const QUIET_MS = 60_000;

interface SessionExpiryState {
  expired: boolean;
  dismissedAt: number | null;
  expire: (now?: number) => void;
  dismiss: (now?: number) => void;
  reset: () => void;
}

/** Whether the "You've been signed out" dialog (Figma P01) is up. Raised by `expiringFetch` on a 401. */
export const useSessionExpiry = create<SessionExpiryState>((set, get) => ({
  expired: false,
  dismissedAt: null,
  expire: (now = Date.now()) => {
    const { dismissedAt } = get();
    if (dismissedAt !== null && now - dismissedAt < QUIET_MS) return;
    set({ expired: true });
  },
  dismiss: (now = Date.now()) => set({ expired: false, dismissedAt: now }),
  reset: () => set({ expired: false, dismissedAt: null }),
}));

let wasSignedIn: () => boolean = () => false;

/** Tells the fetch wrapper how to know someone was signed in (the cached session), without importing the router. */
export function watchSessionExpiry(probe: () => boolean) {
  wasSignedIn = probe;
}

/**
 * `fetch` for the API client: a 401 for someone who was signed in means the session ended (12 hours idle, signed out
 * elsewhere) and raises the sign-in dialog. `REAUTH_REQUIRED` is the other 401: still signed in, just asked for the
 * password again, which the password prompt already handles. A 401 for a visitor who never signed in is normal.
 */
export const expiringFetch: typeof fetch = async (input, init) => {
  const res = await fetch(input, init);
  if (res.status === 401 && wasSignedIn()) {
    const code = await res
      .clone()
      .json()
      .then((body: { code?: string } | null) => body?.code)
      .catch(() => undefined);
    if (code !== 'REAUTH_REQUIRED') useSessionExpiry.getState().expire();
  }
  return res;
};
